/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

import type { SpecificEventListener } from "@/types";
import { getComponentConfig, getComponentElements } from "@/utils/dom/component";
import { Drawer, type DrawerEvents, type DrawerOptions } from "./drawer";

export function initMobileMenu() {
    getComponentElements(MobileMenu.selector).forEach(element => new MobileMenu(element));
}

export class MobileMenu extends Drawer<MobileMenuEvents> {
    static override selector = 'mobile-menu';

    static override defaults: Partial<MobileMenuOptions> = {
        ...Drawer.defaults,
        bodyScrollLockClass: 'mobile-nav-scroll-lock',
        submenuSelector: 'nav li ul',
        submenuOpenClass: '_active',
        submenuCloseSelector: 'button',
        childSubmenuOpenClass: '_section-active'
    };

    declare config: MobileMenuOptions;

    private _submenus: SubMenuData[] = [];
    private _menusByParent = new WeakMap<HTMLElement, SubMenuData[]>();

    constructor(element: HTMLElement, options: Partial<MobileMenuOptions> = {}) {
        element.dataset.drawerId = element.dataset.drawerId || 'mobile-menu';

        super(element, options);

        this._initSubmenus();
    }

    /**
     * @inheritdoc
     */
    public override destroy(): void {
        super.destroy();

        this._submenus.forEach(menu => {
            menu.item.removeEventListener('click', menu.clickListener);
            menu.list.removeEventListener('click', menu.listClickListener);
            menu.closeButton?.removeEventListener('click', menu.clickListener);
        });
        this._submenus = null as any;
        this._menusByParent = null as any;
    }

    /**
     * @inheritdoc
     */
    protected override _initConfig(options: Partial<MobileMenuOptions>) {
        this.config = {
            ...MobileMenu.defaults,
            ...getComponentConfig(MobileMenu.selector, this.element),
            ...options
        };
    }

    /**
     * @inheritdoc
     */
    protected override _onClose(): void {
        super._onClose();
        this._submenus.forEach(menu => menu.toggle(false));
    }

    /**
     * Initialize submenus
     */
    private _initSubmenus() {
        this.element.querySelectorAll<HTMLElement>(this.config.submenuSelector).forEach(list => {
            const item = list.closest<HTMLElement>('li')!;
            let parent = item.closest<HTMLElement>(this.config.submenuSelector) || item.parentElement!;

            if (parent.parentElement!.matches('nav')) {
                parent = this.element;
            }

            const data: SubMenuData = {
                item,
                list,
                parent,
                active: false,
                closeButton: list.querySelector(this.config.submenuCloseSelector),
                toggle: () => {},
                clickListener: () => {},
                listClickListener: (event) => event.stopPropagation()
            };

            data.toggle = (state) => this._toggleSubmenuClass(data, state)
            data.clickListener = (event) => this._toggleSubmenu(data, event);

            this._submenus.push(data);

            let children = this._menusByParent.get(parent);
            if (!children) {
                children = [];
                this._menusByParent.set(parent, children);
            }
            children.push(data);

            item.addEventListener('click', data.clickListener);
            list.addEventListener('click', data.listClickListener);
            data.closeButton?.addEventListener('click', data.clickListener);
        });
    }

    /**
     * Toggle submenu opened state by event
     */
    private _toggleSubmenu(data: SubMenuData, event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();

        requestAnimationFrame(() => {
            (this._menusByParent.get(data.parent) || []).forEach(menu => {
                if (menu !== data) {
                    menu.toggle(false);
                }
            });

            data.toggle(!data.active);
        });
    }

    /**
     * Toggle submenu opened state
     */
    private _toggleSubmenuClass(data: SubMenuData, state?: boolean) {
        state = typeof state == 'boolean' ? state : !data.active;

        data.item.classList.toggle(this.config.submenuOpenClass, state);
        data.active = state;

        data.parent.classList.toggle(this.config.childSubmenuOpenClass, state);

        this.trigger(state ? 'subMenuOpened' : 'subMenuClosed', data);
    }
}

export interface MobileMenuEvents extends DrawerEvents {
    subMenuOpened: SubMenuData
    subMenuClosed: SubMenuData
}

export interface MobileMenuOptions extends DrawerOptions {
    submenuSelector: string
    submenuOpenClass: string
    submenuCloseSelector: string
    childSubmenuOpenClass: string
}

interface SubMenuData {
    item: HTMLElement
    list: HTMLElement
    parent: HTMLElement
    active: boolean
    closeButton: HTMLElement | null
    toggle: (state?: boolean) => void
    clickListener: SpecificEventListener<'click'>
    listClickListener: SpecificEventListener<'click'>
}
